import { CSSProperties, memo, ReactNode, useContext, useEffect, useMemo } from 'react'
import { UserLayoutLink, UserLayoutSC, UserLayoutSCProps } from 'components/ClientLayout/styled'
import LayoutMenu from 'components/ui/LayoutMenu'
import { Row } from 'components/ui/Grid'
import Dropdown from 'components/ui/Dropdown'
import { authEventEmitter, AuthDataContext } from 'pages'
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import { ReactComponent as DashboardSVG } from 'components/ClientLayout/assets/dashboard.svg'
import { ReactComponent as ProductsSVG } from 'components/ClientLayout/assets/products.svg'
import { ReactComponent as WelcomeSVG } from 'components/ClientLayout/assets/house.svg'
import { ReactComponent as GallerySVG } from 'components/ClientLayout/assets/gallery.svg'
import { ReactComponent as StarSVG } from 'components/ClientLayout/assets/star.svg'
import { canAccessSupportPage } from 'utils/supportPageAccess'
import Avatar from 'components/ui/Avatar'
import NotificationBell from 'components/NotificationBell'
import { margin } from 'utils/styling/indents'
import { useUserQuery } from 'components/NotificationBell/hooks'
import config from 'lib/config'
import PaywallBanner from 'components/PaywallBanner'
import PaywallGate from 'components/PaywallGate'
import { canUsePaywall } from 'utils/paywallAccess'
import { useFeaturePlan } from 'components/FeatureEntitlements'

export interface ClientLayoutProps extends UserLayoutSCProps {
  className?: string
  style?: CSSProperties
  children?: ReactNode
}
function killswitch() {
  localStorage.clear()
  sessionStorage.clear()
  const banner = document.querySelector('.banner')
  if (banner) {
    banner.className = 'banner hidden'
  }
  location.reload()
}

function ClientLayout({ className, style, children }: ClientLayoutProps) {
  const history = useHistory()
  const location = useLocation()
  const { data: auth } = useContext(AuthDataContext)
  const featurePlan = useFeaturePlan()
  const galleryOff = featurePlan != null && featurePlan.gallery === 'off'
  const showSupportNav = canAccessSupportPage(config.supportPageReleased, auth?.id)
  const paywallOn = canUsePaywall(auth?.id)
  const { data } = useUserQuery()
  useRouteMatch({ path: '/product/:id' })
  useEffect(() => {
    if (galleryOff && location.pathname.indexOf('/gallery') === 0) {
      history.replace('/dashboard')
    }
  }, [galleryOff, history, location.pathname])
  const actions = useMemo(() => {
    const items: Array<{ label: string; onClick: () => void } | JSX.Element> = [
      {
        label: 'General Info',
        onClick: () => history.push('/settings/general'),
      },
      {
        label: 'Widget settings',
        onClick: () => history.push('/settings/widget'),
      },
      <hr />,
    ]
    if (paywallOn) {
      // Always available when paywall UI is on — not only while paywall is required.
      // Shopify merchants use this for day-to-day View billing / plan changes.
      items.push({
        label: 'Billing',
        onClick: () => history.push('/billing'),
      })
    }
    items.push(
      {
        label: config.legalPage.statusPage.label,
        onClick: () => window.open(config.legalPage.statusPage.link, '_blank'),
      },
      {
        label: config.legalPage.Wiki.label,
        onClick: () => window.open(config.legalPage.Wiki.link, '_blank'),
      },
      {
        label: config.legalPage.technicalSupport.label,
        onClick: () => window.open(config.legalPage.technicalSupport.link, '_blank'),
      },
      {
        label: config.legalPage.expertHours.label,
        onClick: () => window.open(config.legalPage.expertHours.link, '_blank'),
      },
      {
        label: config.legalPage.termsOfUse.label,
        onClick: () => window.open(config.legalPage.termsOfUse.link, '_blank'),
      },
      {
        label: config.legalPage.privacyPolicy.label,
        onClick: () => window.open(config.legalPage.privacyPolicy.link, '_blank'),
      },
      <hr />,
      {
        label: 'Logout',
        onClick: () => {
          authEventEmitter.emit('logout')
          killswitch()
        },
      }
    )
    return items
  }, [history, paywallOn])

  return (
    <UserLayoutSC className={className} style={style}>
      <LayoutMenu
        logoHref='/dashboard'
        menu={
          <Row>
            <UserLayoutLink activeClassName='active' to='/welcome'>
              <WelcomeSVG /> Welcome
            </UserLayoutLink>
            <UserLayoutLink activeClassName='active' to='/dashboard'>
              <DashboardSVG /> Dashboard
            </UserLayoutLink>
            <UserLayoutLink activeClassName='active' to='/products'>
              <ProductsSVG /> Products
            </UserLayoutLink>
            {galleryOff ? null : (
              <UserLayoutLink activeClassName='active' to='/gallery'>
                <GallerySVG /> Gallery
              </UserLayoutLink>
            )}
            {showSupportNav ? (
              <UserLayoutLink activeClassName='active' to='/support'>
                <StarSVG /> Support
              </UserLayoutLink>
            ) : null}
          </Row>
        }
        actions={
          <Row>
            <NotificationBell style={margin(4, 18)} />
            <Dropdown options={actions}>
              <Avatar
                src={data?.user.image}
                className='avatar'
                size={44}
                firstName={data?.user.first_name}
              />
            </Dropdown>
          </Row>
        }
      />
      <div className='layout-content-wrapper'>
        <div className='layout-content'>
          <PaywallGate userId={auth?.id}>{children}</PaywallGate>
        </div>
      </div>
      <PaywallBanner enabled={paywallOn} />
    </UserLayoutSC>
  )
}

export default memo(ClientLayout)
